import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { API } from "../constant/api";
import sessionAxios from "../utils/sessionAxios";
import { clearSelectedBusiness } from "../hooks/use-businesses";
import { ContextValueType, PropsType } from "../types/index";

export const AuthContext = createContext<ContextValueType>({});

export const useAuth = () => useContext(AuthContext);

/**
 * The session lives in an httpOnly cookie set by the backend, so JS can't read it. That
 * means "am I logged in?" can no longer be answered locally — it's answered by asking the
 * API. GET /api/users/me is the designated bootstrap: 200 = logged in, 401 = not.
 *
 * This replaces the old `merchant-token` cookie and its "" / "none" / <jwt> tri-state.
 *
 * Uses the bare sessionAxios rather than useAuthAxios, which depends on this context.
 */
export const AuthProvider: React.FC<PropsType> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const refreshSession = useCallback(async () => {
    try {
      const response = await sessionAxios.get(API.getUser);
      // Seed the cache user-context reads from, so boot costs one request, not two.
      queryClient.setQueryData(["merchantDetails"], response.data.data);
      setIsAuthenticated(true);
      return true;
    } catch {
      setIsAuthenticated(false);
      queryClient.removeQueries({ queryKey: ["merchantDetails"] });
      return false;
    } finally {
      setAuthReady(true);
    }
  }, [queryClient]);

  useEffect(() => {
    // Existing merchants still carry the pre-migration `merchant-token` cookie, which was
    // JS-readable and holds a live JWT. Bin it — the httpOnly cookie is the session now.
    document.cookie = "merchant-token=; Max-Age=0; path=/";
    refreshSession();
  }, [refreshSession]);

  /**
   * Called after a successful login response. The backend has already set the cookie —
   * there is no token for us to store, so this just re-reads the session.
   */
  const authenticate = useCallback(async () => {
    const ok = await refreshSession();
    if (!ok) {
      throw new Error("Signed in, but no session was established.");
    }
    return "";
  }, [refreshSession]);

  /**
   * We can't delete an httpOnly cookie ourselves — the server has to, and it revokes the
   * session at the same time. Local state is cleared regardless of the response so a failed
   * request can never strand someone in a logged-in-looking UI.
   */
  const logout = useCallback(async () => {
    try {
      await sessionAxios.post(API.logout);
    } catch {
      // Already expired or revoked — nothing left to clear server-side.
    }
    setIsAuthenticated(false);
    // Drop every cached query, or the next user in this tab briefly sees the last one's data.
    queryClient.clear();
    clearSelectedBusiness();
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        authReady,
        authenticate,
        refreshSession,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
